
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const tiers = [
  {
    amount: '₹500',
    icon: 'ri-capsule-line',
    title: 'Oxygen Starter Kit',
    description: 'Funds one month\'s supply of oxygen supplements for a child in a rural community.',
    impact: '1 Child Supported',
    color: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    families: 1,
  },
  {
    amount: '₹1,500',
    icon: 'ri-home-heart-line',
    title: 'Family Wellness Pack',
    description: 'Provides a complete OxyKit for an entire family for one month, including consultations.',
    impact: '1 Family for 1 Month',
    color: 'from-teal-400 to-cyan-500',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    families: 3,
    featured: true,
  },
  {
    amount: '₹5,000',
    icon: 'ri-community-line',
    title: 'Village Health Drive',
    description: 'Sponsors an ASHA worker\'s outreach visit to a village, reaching up to 10 families.',
    impact: '10 Families Reached',
    color: 'from-cyan-400 to-sky-500',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    families: 10,
  },
];

const steps = [
  {
    icon: 'ri-hand-coin-line',
    title: 'You Donate',
    description: 'Your contribution is received securely and allocated directly to oxygen product procurement.',
  },
  {
    icon: 'ri-store-2-line',
    title: 'We Procure',
    description: 'We source high-quality oxygen supplements and OxyKits at bulk rates to maximise reach.',
  },
  {
    icon: 'ri-truck-line',
    title: 'We Distribute',
    description: 'Products are delivered through our network of 150+ PHCs and 2,500+ ASHA workers.',
  },
  {
    icon: 'ri-family-line',
    title: 'Families Benefit',
    description: 'Families in underserved communities receive life-changing oxygen wellness support.',
  },
];

export default function DonationImpactSection() {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block bg-[#12B6B6]/10 text-[#12B6B6] text-sm font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
            Donation Impact
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-5 leading-tight">
            How Your Donation <span className="text-[#12B6B6]">Funds Oxygen</span> for Families
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Every rupee you give goes directly toward putting oxygen supplementation products 
            in the hands of families who cannot afford them. Here's exactly how it works.
          </p>
        </div>

        {/* Journey Steps */}
        <div className="relative mb-20">
          {/* Connector line */}
          <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-[#12B6B6]/20 via-[#12B6B6] to-[#12B6B6]/20 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#12B6B6] text-white shadow-lg mb-5 ring-4 ring-white">
                  <i className={`${step.icon} text-2xl`}></i>
                </div>
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#12B6B6]/10 text-[#12B6B6] text-xs font-bold mb-3">
                  {index + 1}
                </div>
                <h4 className="text-lg font-bold text-[#111111] mb-2">{step.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Donation Tiers */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-[#111111] text-center mb-10">
            See What Your Donation Achieves
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier, index) => (
              <div
                key={index}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                className={`relative rounded-2xl border-2 p-8 transition-all duration-300 cursor-pointer
                  ${tier.featured ? 'border-[#12B6B6] shadow-xl scale-105' : `${tier.border} hover:shadow-lg hover:-translate-y-1`}
                  ${hovered === index && !tier.featured ? 'border-[#12B6B6]/50' : ''}
                  ${tier.bg}`}
              >
                {tier.featured && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#12B6B6] text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${tier.color} text-white mb-5 shadow-md`}>
                  <i className={`${tier.icon} text-2xl`}></i>
                </div>

                <div className="text-3xl font-extrabold text-[#111111] mb-1">{tier.amount}</div>
                <div className="text-base font-semibold text-[#12B6B6] mb-3">{tier.title}</div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">{tier.description}</p>

                <div className="flex items-center space-x-2 bg-white/70 rounded-xl px-4 py-2.5 mb-6">
                  <i className="ri-checkbox-circle-fill text-[#12B6B6] text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm font-semibold text-[#111111]">{tier.impact}</span>
                </div>

                <button
                  onClick={() => navigate('/donate')}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 whitespace-nowrap
                    ${tier.featured
                      ? 'bg-[#12B6B6] text-white hover:bg-[#0fa3a3] shadow-md'
                      : 'bg-white text-[#12B6B6] border border-[#12B6B6] hover:bg-[#12B6B6] hover:text-white'
                    }`}
                >
                  Donate {tier.amount}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Transparency Banner */}
        <div className="bg-gradient-to-r from-[#12B6B6]/10 via-[#12B6B6]/5 to-[#12B6B6]/10 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-start space-x-5">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#12B6B6] text-white flex-shrink-0">
              <i className="ri-shield-check-line text-2xl"></i>
            </div>
            <div>
              <h4 className="text-xl font-bold text-[#111111] mb-2">100% Transparent Allocation</h4>
              <p className="text-gray-600 text-sm leading-relaxed max-w-xl">
                We publish quarterly impact reports showing exactly how every donation is spent. 
                Over <strong>92% of all funds</strong> go directly to product procurement and distribution — 
                not administration.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/donate')}
            className="bg-[#12B6B6] text-white px-8 py-3.5 rounded-xl font-semibold text-sm hover:bg-[#0fa3a3] transition-colors whitespace-nowrap shadow-md flex-shrink-0 cursor-pointer"
          >
            Donate to Help Families
          </button>
        </div>

      </div>
    </section>
  );
}
